import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, ClipboardCheck, Lock, NotebookPen, Pencil, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { LoadingState, ErrorState } from "@/components/common/States";
import { ClassFormDialog } from "@/components/classes/ClassFormDialog";
import { AttendanceDialog } from "@/components/classes/AttendanceDialog";
import { NotesDialog, NOTE_FIELDS } from "@/components/classes/NotesDialog";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { brl, endTime, fmtDate, weekdayName } from "@/lib/format";

const ATT = { presente: "Presente", ausente: "Ausente", cancelada: "Aula cancelada", reagendada: "Aula reagendada" };

export const ClassDetailDialog = ({ lessonId, open, onOpenChange, onChanged }) => {
  const { hasFeature } = useAuth();
  const { data: lesson, loading, error, reload } = useFetch(lessonId ? `/classes/${lessonId}` : null, { skip: !open });
  const [modal, setModal] = useState(null);

  const changed = () => {
    reload();
    onChanged?.();
  };

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      changed();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = () => run(async () => { await api.delete(`/classes/${lessonId}`); onOpenChange(false); }, "Aula excluída");
  const canAttend = hasFeature("presenca");

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="class-detail-dialog">
          <DialogHeader><DialogTitle>Detalhes da aula</DialogTitle></DialogHeader>
          {loading || !lesson ? (error ? <ErrorState error={error} /> : <LoadingState rows={2} />) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link to={`/alunos/${lesson.student_id}`} className="font-heading text-lg font-semibold hover:text-primary" data-testid="class-detail-student-link">{lesson.student_name}</Link>
                  <p className="text-sm text-muted-foreground">{lesson.subject} · {lesson.type === "online" ? "Online" : "Presencial"}</p>
                </div>
                <StatusBadge status={lesson.status} testId="class-detail-status" />
              </div>
              <div className="grid grid-cols-2 gap-3 rounded-xl bg-muted/50 p-4 text-sm">
                <div><span className="text-muted-foreground">Data</span><p className="font-medium">{weekdayName(lesson.date)}, {fmtDate(lesson.date)}</p></div>
                <div><span className="text-muted-foreground">Horário</span><p className="font-mono font-medium">{lesson.start_time} – {endTime(lesson.start_time, lesson.duration)}</p></div>
                <div><span className="text-muted-foreground">Duração</span><p className="font-medium">{lesson.duration} min</p></div>
                <div><span className="text-muted-foreground">Valor</span><p className="font-mono font-medium">{brl(lesson.price)}</p></div>
              </div>
              {lesson.package_id && <p className="text-xs text-muted-foreground">Aula vinculada a um pacote de aulas.</p>}
              {lesson.rescheduled_from && <p className="text-xs text-muted-foreground">Reagendada de {fmtDate(lesson.rescheduled_from.slice(0, 10))} {lesson.rescheduled_from.slice(11)}</p>}
              {lesson.notes && <p className="text-sm"><span className="text-muted-foreground">Observações: </span>{lesson.notes}</p>}
              {lesson.attendance && (
                <div className="rounded-lg border p-3 text-sm" data-testid="class-detail-attendance">
                  <span className="font-semibold">Presença: </span>{ATT[lesson.attendance.status]}
                  {lesson.attendance.notes && <p className="mt-1 text-muted-foreground">{lesson.attendance.notes}</p>}
                </div>
              )}
              {lesson.note && (
                <div className="space-y-1 rounded-lg border p-3 text-sm" data-testid="class-detail-notes">
                  {NOTE_FIELDS.filter(([k]) => lesson.note[k]).map(([k, label]) => <p key={k}><span className="font-semibold">{label}: </span>{lesson.note[k]}</p>)}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {lesson.status === "agendada" && (
                  <Button variant="outline" onClick={() => run(() => api.patch(`/classes/${lessonId}/status`, { status: "confirmada" }), "Aula confirmada")} data-testid="class-confirm-btn">
                    <CheckCircle2 className="mr-2 h-4 w-4" />Confirmar
                  </Button>
                )}
                <Button variant="outline" disabled={!canAttend} onClick={() => setModal("attendance")} data-testid="class-attendance-btn">
                  {canAttend ? <ClipboardCheck className="mr-2 h-4 w-4" /> : <Lock className="mr-2 h-4 w-4" />}Presença
                </Button>
                <Button variant="outline" onClick={() => setModal("notes")} data-testid="class-notes-btn"><NotebookPen className="mr-2 h-4 w-4" />Anotações</Button>
                <Button variant="outline" onClick={() => setModal("edit")} data-testid="class-edit-btn"><Pencil className="mr-2 h-4 w-4" />Editar / Reagendar</Button>
                {lesson.status !== "cancelada" && (
                  <Button variant="outline" onClick={() => setModal("cancel")} data-testid="class-cancel-btn"><XCircle className="mr-2 h-4 w-4" />Cancelar aula</Button>
                )}
                <Button variant="ghost" className="text-destructive" onClick={() => setModal("delete")} data-testid="class-delete-btn"><Trash2 className="mr-2 h-4 w-4" />Excluir</Button>
              </div>
              {!canAttend && <p className="flex items-center gap-1 text-xs text-muted-foreground"><CalendarClock className="h-3 w-3" />Controle de presença disponível a partir do plano Básico.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ClassFormDialog open={modal === "edit"} onOpenChange={(o) => !o && setModal(null)} lesson={lesson} onSaved={changed} />
      <AttendanceDialog open={modal === "attendance"} onOpenChange={(o) => !o && setModal(null)} lesson={lesson} onSaved={changed} />
      <NotesDialog open={modal === "notes"} onOpenChange={(o) => !o && setModal(null)} lesson={lesson} onSaved={changed} />
      <ConfirmDialog open={modal === "cancel"} onOpenChange={(o) => !o && setModal(null)} title="Cancelar esta aula?"
        description="A aula ficará marcada como cancelada e o horário será liberado." confirmLabel="Cancelar aula" destructive
        onConfirm={() => run(() => api.post(`/classes/${lessonId}/cancel`), "Aula cancelada")} />
      <ConfirmDialog open={modal === "delete"} onOpenChange={(o) => !o && setModal(null)} title="Excluir esta aula?"
        description="Esta ação não pode ser desfeita." confirmLabel="Excluir" destructive onConfirm={remove} />
    </>
  );
};
